'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios, { AxiosError } from 'axios';
import pLimit from 'p-limit';
import { Effect } from 'effect';
import { callKnowledgeDocuments } from '@/entities/knowledge-document';
import { validateFiles, DEFAULT_VALIDATION_RULES, type FileValidationRules } from './file-validation';
export interface UploadTask {
  id: string;
  fileName: string;
  fileSize: number;
  folderId: string;
  status: 'queued' | 'preparing' | 'uploading' | 'completing' | 'complete' | 'error' | 'cancelled';
  progress: number;
  error?: string;
  abortController?: AbortController;
  fileId?: string; // Store fileId for retry
}
interface FileUploadContextValue {
  tasks: UploadTask[];
  uploadFile: (file: File, folderId: string, existingFileId?: string) => Promise<{
    success: boolean;
    fileId?: string;
  }>;
  uploadMultipleFiles: (files: File[], folderId: string) => Promise<void>;
  removeTask: (taskId: string) => void;
  clearCompleted: () => void;
  cancelUpload: (taskId: string) => void;
  retryUpload: (taskId: string) => Promise<void>;
  triggerFileSelect: (folderId: string, options?: {
    multiple?: boolean;
  }) => void;
  validationRules: FileValidationRules;
}
const FileUploadContext = createContext<FileUploadContextValue | null>(null);

// Limit concurrent uploads to prevent browser/server overload
const CONCURRENT_UPLOADS = 3;
export function FileUploadProvider(t0) {
  const $ = _c(42);
  const {
    children,
    validationRules: t1
  } = t0;
  const validationRules = t1 === undefined ? DEFAULT_VALIDATION_RULES : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [tasks, setTasks] = useState(t2);
  const fileInputRef = useRef(null);
  const currentFolderRef = useRef("root");
  const queryClient = useQueryClient();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = pLimit(CONCURRENT_UPLOADS);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const uploadLimit = useRef(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const filesRef = useRef(t4);
  const tasksRef = useRef(tasks);
  let t5;
  let t6;
  if ($[3] !== tasks) {
    t5 = () => {
      tasksRef.current = tasks;
    };
    t6 = [tasks];
    $[3] = tasks;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[6] !== tasks) {
    t7 = () => {
      const handleBeforeUnload = e => {
        const activeUploads = Array.from(tasks.values()).some(_temp);
        if (activeUploads) {
          e.preventDefault();
          e.returnValue = "";
          return "";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    t8 = [tasks];
    $[6] = tasks;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const files = filesRef.current;
      const latestTasks = tasksRef;
      return () => {
        latestTasks.current.forEach(_temp2);
        files.clear();
      };
    };
    t10 = [];
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  useEffect(t9, t10);
  let t11;
  if ($[11] !== queryClient) {
    t11 = async (file, folderId, existingFileId) => {
      const taskId = crypto.randomUUID();
      const abortController = new AbortController();
      filesRef.current.set(taskId, file);
      setTasks(prev => {
        const next = new Map(prev);
        next.set(taskId, {
          id: taskId,
          fileName: file.name,
          fileSize: file.size,
          folderId,
          status: "queued",
          progress: 0,
          abortController
        });
        return next;
      });
      ;
      try {
        await uploadLimit.current(async () => {
          const currentTask = tasksRef.current.get(taskId);
          if (currentTask?.status === "cancelled") {
            throw new Error("Upload cancelled");
          }
          setTasks(prev_2 => {
            const next_2 = new Map(prev_2);
            const task_2 = next_2.get(taskId);
            if (task_2) {
              next_2.set(taskId, {
                ...task_2,
                status: "preparing"
              });
            }
            return next_2;
          });
          const prepare = {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type || "application/octet-stream",
            folderId
          };
          const {
            fileId,
            uploadUrl
          } = await callKnowledgeDocuments(queryClient, rpc => existingFileId ? rpc.retryKnowledgeUpload({
            fileId: existingFileId
          }).pipe(Effect.catchTag("KnowledgeDocumentsWireError", error_0 => error_0.errorCode === "DOCUMENTS_NOT_FOUND" ? rpc.prepareKnowledgeUpload(prepare) : Effect.fail(error_0))) : rpc.prepareKnowledgeUpload(prepare), {
            signal: abortController.signal
          });
          setTasks(prev_3 => {
            const next_3 = new Map(prev_3);
            const task_3 = next_3.get(taskId);
            if (task_3) {
              next_3.set(taskId, {
                ...task_3,
                fileId
              });
            }
            return next_3;
          });
          setTasks(prev_4 => {
            const next_4 = new Map(prev_4);
            const task_4 = next_4.get(taskId);
            if (task_4) {
              next_4.set(taskId, {
                ...task_4,
                status: "uploading"
              });
            }
            return next_4;
          });
          await axios.put(uploadUrl, file, {
            headers: {
              "Content-Type": file.type || "application/octet-stream"
            },
            signal: abortController.signal,
            onUploadProgress: progressEvent => {
              const percentCompleted = progressEvent.total ? Math.round(progressEvent.loaded * 100 / progressEvent.total) : 0;
              setTasks(prev_5 => {
                const next_5 = new Map(prev_5);
                const task_5 = next_5.get(taskId);
                if (task_5) {
                  next_5.set(taskId, {
                    ...task_5,
                    progress: percentCompleted
                  });
                }
                return next_5;
              });
            }
          });
          setTasks(prev_6 => {
            const next_6 = new Map(prev_6);
            const task_6 = next_6.get(taskId);
            if (task_6) {
              next_6.set(taskId, {
                ...task_6,
                status: "completing",
                progress: 95
              });
            }
            return next_6;
          });
          await callKnowledgeDocuments(queryClient, rpc_0 => rpc_0.completeKnowledgeUpload({
            fileId
          }), {
            signal: abortController.signal
          });
          setTasks(prev_7 => {
            const next_7 = new Map(prev_7);
            const task_7 = next_7.get(taskId);
            if (task_7) {
              next_7.set(taskId, {
                ...task_7,
                status: "complete",
                progress: 100,
                abortController: undefined
              });
            }
            return next_7;
          });
          filesRef.current.delete(taskId);
          return {
            success: true,
            fileId
          };
        });
        return {
          success: true
        };
      } catch (t12) {
        const error = t12;
        if (abortController.signal.aborted || axios.isCancel(error) || (error as Error).message === "Upload cancelled") {
          setTasks(prev_0 => {
            const next_0 = new Map(prev_0);
            const task_0 = next_0.get(taskId);
            if (task_0) {
              next_0.set(taskId, {
                ...task_0,
                status: "cancelled",
                abortController: undefined
              });
            }
            return next_0;
          });
          return {
            success: false
          };
        }
        let errorMessage = "Unknown error";
        if (error instanceof AxiosError) {
          errorMessage = error.response?.data?.error || error.message;
          if (error.response?.status === 403 && error.config?.url?.includes("amazonaws.com")) {
            errorMessage = "Upload URL expired. Please try again.";
          }
        } else {
          if (error instanceof Error) {
            errorMessage = error.message;
          }
        }
        setTasks(prev_1 => {
          const next_1 = new Map(prev_1);
          const task_1 = next_1.get(taskId);
          if (task_1) {
            next_1.set(taskId, {
              ...task_1,
              status: "error",
              error: errorMessage,
              abortController: undefined
            });
          }
          return next_1;
        });
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
        return {
          success: false
        };
      }
    };
    $[11] = queryClient;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const uploadFile = t11;
  let t12;
  if ($[13] !== queryClient || $[14] !== uploadFile || $[15] !== validationRules) {
    t12 = async (files_0, folderId_0) => {
      const {
        valid,
        errors
      } = validateFiles(files_0, validationRules);
      if (errors.length > 0) {
        errors.forEach(_temp3);
        if (valid.length === 0) {
          return;
        }
      }
      const results = await Promise.allSettled(valid.map(file_0 => uploadFile(file_0, folderId_0)));
      const successCount = results.filter(_temp4).length;
      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? "s" : ""}`);
        queryClient.invalidateQueries({
          queryKey: ["kb-hub-documents"]
        });
      }
    };
    $[13] = queryClient;
    $[14] = uploadFile;
    $[15] = validationRules;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const uploadMultipleFiles = t12;
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = taskId_0 => {
      setTasks(prev_8 => {
        const next_8 = new Map(prev_8);
        next_8.delete(taskId_0);
        return next_8;
      });
      filesRef.current.delete(taskId_0);
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const removeTask = t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setTasks(prev_9 => {
        const next_9 = new Map(prev_9);
        Array.from(next_9.entries()).forEach(t15 => {
          const [id, task_8] = t15;
          if (task_8.status === "complete" || task_8.status === "cancelled") {
            next_9.delete(id);
            filesRef.current.delete(id);
          }
        });
        return next_9;
      });
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const clearCompleted = t14;
  let t15;
  if ($[19] !== tasks) {
    t15 = taskId_1 => {
      const task_9 = tasks.get(taskId_1);
      if (task_9?.abortController && (task_9.status === "uploading" || task_9.status === "preparing" || task_9.status === "completing")) {
        task_9.abortController.abort();
      }
    };
    $[19] = tasks;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const cancelUpload = t15;
  let t16;
  if ($[21] !== tasks || $[22] !== uploadFile) {
    t16 = async taskId_2 => {
      const task_10 = tasks.get(taskId_2);
      const file_1 = filesRef.current.get(taskId_2);
      if (!task_10 || !file_1) {
        toast.error("Cannot retry: file not found");
        return;
      }
      if (task_10.status !== "error") {
        toast.error("Can only retry failed uploads");
        return;
      }
      const existingFileId_0 = task_10.fileId;
      if (!existingFileId_0) {
        toast.error("Cannot retry: no file ID found");
        return;
      }
      console.log(`Retrying upload: taskId=${taskId_2}, fileId=${existingFileId_0}`);
      removeTask(taskId_2);
      await uploadFile(file_1, task_10.folderId, existingFileId_0);
    };
    $[21] = tasks;
    $[22] = uploadFile;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const retryUpload = t16;
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = (folderId_1, options) => {
      currentFolderRef.current = folderId_1;
      if (fileInputRef.current) {
        fileInputRef.current.multiple = options?.multiple ?? true;
        fileInputRef.current.click();
      }
    };
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const triggerFileSelect = t17;
  let t18;
  if ($[25] !== uploadMultipleFiles) {
    t18 = event => {
      const files_1 = Array.from(event.target.files || []);
      const folderId_2 = currentFolderRef.current;
      if (files_1.length > 0) {
        uploadMultipleFiles(files_1, folderId_2);
      }
      event.target.value = "";
    };
    $[25] = uploadMultipleFiles;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleFileChange = t18;
  let t19;
  if ($[27] !== tasks) {
    t19 = Array.from(tasks.values());
    $[27] = tasks;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== cancelUpload || $[30] !== retryUpload || $[31] !== t19 || $[32] !== uploadFile || $[33] !== uploadMultipleFiles || $[34] !== validationRules) {
    t20 = {
      tasks: t19,
      uploadFile,
      uploadMultipleFiles,
      removeTask,
      clearCompleted,
      cancelUpload,
      retryUpload,
      triggerFileSelect,
      validationRules
    };
    $[29] = cancelUpload;
    $[30] = retryUpload;
    $[31] = t19;
    $[32] = uploadFile;
    $[33] = uploadMultipleFiles;
    $[34] = validationRules;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== handleFileChange) {
    t21 = <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} multiple={true} aria-label="File upload input" />;
    $[36] = handleFileChange;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== children || $[39] !== t20 || $[40] !== t21) {
    t22 = <FileUploadContext.Provider value={t20}>{t21}{children}</FileUploadContext.Provider>;
    $[38] = children;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  return t22;
}
function _temp4(r) {
  return r.status === "fulfilled" && r.value.success;
}
function _temp3(error_1) {
  return toast.error(error_1);
}
function _temp2(task) {
  if (task.abortController && task.status === "uploading") {
    task.abortController.abort();
  }
}
function _temp(t) {
  return t.status === "uploading" || t.status === "preparing" || t.status === "completing" || t.status === "queued";
}
export function useFileUploadContext() {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("useFileUploadContext must be used within FileUploadProvider");
  }
  return context;
}